import { useState, type CSSProperties } from "react";
import { useQuery } from "@tanstack/react-query";
import { Breadcrumb } from "@/components/ui/Breadcrumb";
import { Icon } from "@/components/ui/Icon";
import { PostfachCard } from "@/components/postfach/PostfachCard";
import { TicketDetailOverlay } from "@/components/board/TicketDetailOverlay";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { t } from "@/lib/strings";
import type { MySubmissionItem, UUID } from "@/lib/types";

export function PostfachPage() {
  const isDesktop = useBreakpoint("md");
  const [openTicket, setOpenTicket] = useState<{ boardId: UUID; rowId: UUID } | null>(null);
  const query = useQuery({
    queryKey: queryKeys.mySubmissions,
    queryFn: () => api.listMySubmissions(),
  });
  const items: MySubmissionItem[] = query.data ?? [];

  return (
    <div
      style={{
        flex: 1,
        overflow: "auto",
        padding: isDesktop ? "28px 36px 80px" : "16px 12px 80px",
        background: "var(--bg)",
      }}
    >
      <div style={{ maxWidth: 820, margin: "0 auto" }}>
        <div style={{ marginBottom: 10 }}>
          <Breadcrumb items={[{ label: t.navHome, to: "/" }, { label: t.navPostfach }]} />
        </div>

        <h1
          style={{
            margin: "0 0 4px",
            fontSize: 24,
            fontWeight: 600,
            color: "var(--ink-1)",
            letterSpacing: "-0.015em",
          }}
        >
          {t.postfachTitle}
        </h1>
        <p style={{ margin: "0 0 22px", fontSize: 13, color: "var(--ink-3)" }}>
          {t.postfachSubtitle}
        </p>

        {query.isLoading ? (
          <SkeletonList />
        ) : items.length === 0 ? (
          <EmptyState />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {items.map((it) => (
              <PostfachCard
                key={it.row_id}
                item={it}
                isDesktop={isDesktop}
                onOpen={(boardId, rowId) => setOpenTicket({ boardId, rowId })}
              />
            ))}
          </div>
        )}
      </div>

      {openTicket ? (
        <TicketDetailOverlay
          boardId={openTicket.boardId}
          rowId={openTicket.rowId}
          onClose={() => setOpenTicket(null)}
        />
      ) : null}
    </div>
  );
}

function EmptyState() {
  return (
    <div
      style={{
        background: "var(--surface)",
        borderRadius: 10,
        border: "1px dashed var(--hairline-strong)",
        padding: "40px 24px",
        textAlign: "center",
      }}
    >
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: 999,
          background: "var(--surface-soft)",
          color: "var(--ink-3)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 14,
        }}
      >
        <Icon name="inbox" size={24} color="var(--ink-3)" />
      </div>
      <div style={{ fontSize: 15, fontWeight: 500, color: "var(--ink-1)", marginBottom: 6 }}>
        {t.postfachEmptyTitle}
      </div>
      <div style={{ fontSize: 13, color: "var(--ink-3)" }}>{t.postfachEmptyHint}</div>
    </div>
  );
}

const skeletonRowStyle: CSSProperties = {
  height: 110,
  background: "var(--surface)",
  border: "1px solid var(--hairline)",
  borderRadius: 10,
  marginBottom: 10,
  opacity: 0.55,
};

function SkeletonList() {
  return (
    <div>
      {[0, 1, 2, 3].map((i) => (
        <div key={i} style={skeletonRowStyle} />
      ))}
    </div>
  );
}
